/**
 * Shared authentication when Form Engine Portal is opened or redirected from a host portal (parent app).
 * The host typically uses MSAL; when users open Form Engine Portal in a new tab or iframe
 * (e.g. `/examform?eid=…&efid=…` with `#access_token=…`),
 * we need the same access token for Data Access, Form Data Service, Case Management, and other APIs
 * to avoid "invalid_token" / "The signature is invalid".
 *
 * Token can be provided via:
 * 1. URL: host portal appends token when redirecting (see host integration, e.g. `formEnginePortal.utils.ts`):
 *    - Fragment: https://form-engine-portal-url/#access_token=<token>
 *    - Query: https://form-engine-portal-url/?access_token=<token>
 *    (Token is read on load and removed from URL.)
 * 2. postMessage (when FormEnginePortal is in an iframe):
 *    window.postMessage({ type: HOST_PORTAL_AUTH_MESSAGE_TYPE, accessToken: '<token>' }, targetOrigin);
 * 3. Storage transfer: when opened via window.open, FormEnginePortal requests storage from the opener (host portal).
 *
 * PostMessage payload `type` string values are fixed for interoperability with existing host and CaseMgmtPortal integrations.
 *
 * Contract aligns with CaseMgmtPortal sharedAuth where applicable (same param names and message types).
 */

import { HOST_PORTAL_URL, CASE_MGMT_PORTAL_URL } from '../config/api.config';
import { PORTAL_APPEARANCE_FROM_OPENER_EVENT } from '../constants/appearanceQueryConstants';
import { readBrandAccentFromStorage } from '../constants/storageKeyConstants';

/** URL param/fragment name for access token. Must match host portal Form Engine integration utilities. */
export const SHARED_AUTH_ACCESS_TOKEN_PARAM = 'access_token';

/** sessionStorage key for shared token (same-origin or after reading from URL/postMessage). */
export const SHARED_ACCESS_TOKEN_KEY = 'healthesus_shared_access_token';

/**
 * postMessage type for host portal parent to send token.
 * String value must remain `HEALTHCARE_PORTAL_AUTH` for wire compatibility with host and CaseMgmtPortal.
 */
export const HOST_PORTAL_AUTH_MESSAGE_TYPE = 'HEALTHCARE_PORTAL_AUTH' as const;

/** @deprecated Use HOST_PORTAL_AUTH_MESSAGE_TYPE */
export const HEALTHCARE_PORTAL_AUTH_MESSAGE_TYPE = HOST_PORTAL_AUTH_MESSAGE_TYPE;

/** Custom event dispatched when shared token is set so AuthContext/API client can use it. */
export const SHARED_AUTH_TOKEN_SET_EVENT = 'healthesus-shared-auth-token-set';

/**
 * postMessage: FormEnginePortal requests localStorage/sessionStorage from opener (host portal).
 * String value must remain `HEALTHCARE_PORTAL_REQUEST_STORAGE` for wire compatibility.
 */
export const STORAGE_TRANSFER_REQUEST = 'HEALTHCARE_PORTAL_REQUEST_STORAGE' as const;
/**
 * postMessage: opener sends localStorage/sessionStorage to FormEnginePortal (same keys).
 * String value must remain `HEALTHCARE_PORTAL_STORAGE_TRANSFER` for wire compatibility.
 */
export const STORAGE_TRANSFER_RESPONSE = 'HEALTHCARE_PORTAL_STORAGE_TRANSFER' as const;

/** In-memory fallback when token is received via postMessage. */
let inMemorySharedToken: string | null = null;

/**
 * Get the shared access token (from sessionStorage or in-memory).
 * Used by apiClient and AuthContext when MSAL has no accounts.
 */
export function getSharedAccessToken(): string | null {
  try {
    if (inMemorySharedToken) return inMemorySharedToken;
    return sessionStorage.getItem(SHARED_ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Store the shared access token so API calls use the same token as the host portal session.
 */
export function setSharedAccessToken(token: string): void {
  try {
    inMemorySharedToken = token;
    sessionStorage.setItem(SHARED_ACCESS_TOKEN_KEY, token);
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent(SHARED_AUTH_TOKEN_SET_EVENT));
    }
    if (import.meta.env.DEV) {
      console.log('[SharedAuth] Token stored for API requests');
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[SharedAuth] Failed to store token:', e);
    }
  }
}

/**
 * Clear the shared token (e.g. on logout).
 */
export function clearSharedAccessToken(): void {
  try {
    inMemorySharedToken = null;
    sessionStorage.removeItem(SHARED_ACCESS_TOKEN_KEY);
  } catch {
    // ignore
  }
}

/**
 * Decode JWT payload without verification (client-side only; API validates the token).
 * Returns oid, sub, and optional email/name for user identity when MSAL accounts are not available.
 */
export function decodeJwtPayload(
  token: string
): { oid?: string; sub?: string; email?: string; name?: string } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const parsed = JSON.parse(json) as Record<string, unknown>;
    return {
      oid: typeof parsed.oid === 'string' ? parsed.oid : undefined,
      sub: typeof parsed.sub === 'string' ? parsed.sub : undefined,
      email: typeof parsed.email === 'string' ? parsed.email : undefined,
      name: typeof parsed.name === 'string' ? parsed.name : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Extract Entra Object ID from a JWT (access or ID token).
 * Prefers oid; falls back to sub if it looks like a UUID.
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function getEntraIdFromToken(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  if (payload.oid && UUID_REGEX.test(payload.oid)) return payload.oid;
  if (payload.sub && UUID_REGEX.test(payload.sub)) return payload.sub;
  return null;
}

/**
 * Parse URL for access_token in fragment (#access_token=...) or query (?access_token=...).
 * Stores token and cleans URL so the token is not visible in address bar or referrer.
 */
function initSharedAuthFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const hash = window.location.hash;
    const search = window.location.search;
    let token: string | null = null;

    if (hash) {
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      token = params.get(SHARED_AUTH_ACCESS_TOKEN_PARAM);
    }
    if (!token && search) {
      const params = new URLSearchParams(search);
      token = params.get(SHARED_AUTH_ACCESS_TOKEN_PARAM);
    }
    if (!token) return;

    setSharedAccessToken(token);
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete(SHARED_AUTH_ACCESS_TOKEN_PARAM);
    const cleanUrl = url.pathname + url.search;
    window.history.replaceState(null, '', cleanUrl);
    if (import.meta.env.DEV) {
      console.log('[SharedAuth] Token read from URL and stored; URL cleaned');
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[SharedAuth] Failed to read token from URL:', e);
    }
  }
}

/**
 * Listen for postMessage from parent (host portal) with access token.
 * Only accepts messages from the configured host portal origin.
 */
function initSharedAuthPostMessage(): void {
  const hostPortalOrigin = getHostPortalOrigin();
  if (!hostPortalOrigin) return;

  window.addEventListener('message', (event) => {
    if (event.origin !== hostPortalOrigin) return;
    if (event.data?.type !== HOST_PORTAL_AUTH_MESSAGE_TYPE) return;

    const token = event.data?.accessToken;
    if (typeof token === 'string' && token.length > 0) {
      setSharedAccessToken(token);
      if (import.meta.env.DEV) {
        console.log('[SharedAuth] Token received via postMessage');
      }
    }
  });
}

/**
 * Request and apply localStorage/sessionStorage from opener (host portal or Case Management Portal).
 * When FormEnginePortal is opened via window.open from the host portal or CaseMgmtPortal, storage is origin-bound
 * so we request it via postMessage. We accept responses from either allowed opener origin.
 */
function initStorageTransferFromOpener(): void {
  if (typeof window === 'undefined') return;
  const opener = window.opener;
  if (!opener || opener.closed) return;

  const hostPortalOrigin = getHostPortalOrigin();
  const caseMgmtOrigin = getCaseMgmtPortalOrigin();
  const allowedOpenerOrigins = [hostPortalOrigin, caseMgmtOrigin].filter(Boolean);
  if (allowedOpenerOrigins.length === 0) return;

  const handleMessage = (event: MessageEvent): void => {
    if (event.source !== opener) return;
    if (!allowedOpenerOrigins.includes(event.origin)) return;
    if (event.data?.type !== STORAGE_TRANSFER_RESPONSE) return;

    try {
      const { localStorage: localData, sessionStorage: sessionData } = event.data;
      if (localData && typeof localData === 'object') {
        Object.entries(localData).forEach(([key, value]) => {
          if (typeof value === 'string') try { localStorage.setItem(key, value); } catch { /* quota/safe */ }
        });
        try {
          const accent = readBrandAccentFromStorage();
          if (accent) {
            document.documentElement.dataset.brand = accent;
          }
        } catch {
          /* ignore */
        }
        if (import.meta.env.DEV) {
          console.log('[SharedAuth] Applied localStorage from opener:', Object.keys(localData).length, 'keys');
        }
      }
      if (sessionData && typeof sessionData === 'object') {
        Object.entries(sessionData).forEach(([key, value]) => {
          if (typeof value === 'string') try { sessionStorage.setItem(key, value); } catch { /* quota/safe */ }
        });
        if (import.meta.env.DEV) {
          console.log('[SharedAuth] Applied sessionStorage from opener:', Object.keys(sessionData).length, 'keys');
        }
      }
      window.removeEventListener('message', handleMessage);
      window.dispatchEvent(new CustomEvent(SHARED_AUTH_TOKEN_SET_EVENT));
      window.dispatchEvent(new CustomEvent(PORTAL_APPEARANCE_FROM_OPENER_EVENT));
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[SharedAuth] Failed to apply transferred storage:', e);
    }
  };

  window.addEventListener('message', handleMessage);
  // Use '*' so the opener (host portal or CaseMgmtPortal) can receive the request regardless of its origin
  opener.postMessage({ type: STORAGE_TRANSFER_REQUEST }, '*');
  if (import.meta.env.DEV) {
    console.log('[SharedAuth] Requested storage transfer from opener');
  }
}

function getCaseMgmtPortalOrigin(): string | null {
  try {
    if (CASE_MGMT_PORTAL_URL && CASE_MGMT_PORTAL_URL.startsWith('http')) {
      return new URL(CASE_MGMT_PORTAL_URL.replace(/\/$/, '')).origin;
    }
  } catch {
    // ignore
  }
  return null;
}

function getHostPortalOrigin(): string | null {
  try {
    if (HOST_PORTAL_URL && HOST_PORTAL_URL.startsWith('http')) {
      return new URL(HOST_PORTAL_URL.replace(/\/$/, '')).origin;
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Initialize shared auth: read token from URL, set up postMessage listener, and request storage from opener.
 * Call once at app startup (e.g. in main.tsx) before AuthProvider mounts.
 */
export function initSharedAuth(): void {
  initSharedAuthFromUrl();
  initSharedAuthPostMessage();
  initStorageTransferFromOpener();
}
